import React from "react";
import { connect } from "react-redux";
import {
  Row,
  Col,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Alert,
} from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import {
  serviceCaller,
  ServiceCallerProps,
  withModals,
} from "../../composition";
import get from "lodash.get";
import { IPost, PostType } from "../../../types/services/IPost";
import { isCurrentUserCollaborator } from "../../../utils/roleCheck";
import s from "./styles.scss";
import IEmailAccount from "../../../types/IEmailAccount";
import { mergeSendingEmailDefault } from "../../../utils/sendingEmailDefault";
import { getSendingPhoneNumber } from "../../../utils/fetch";
import { formatPhoneNumber } from "../../../utils/helpers";
import confirmCss from "react-confirm-alert/src/react-confirm-alert.css";

interface Props extends ServiceCallerProps {
  currentPost?: {
    post: IPost;
    type: PostType;
  };
  canEditPublishing: boolean;
  busyLoading: boolean;
  isVisible: boolean;

  updateEmailSelected: (email: IEmailAccount) => void;
  extraEmailAccounts: Array<IEmailAccount>;
  redisSendingEmail: IEmailAccount | null;
  firebaseDefaultSendingEmail: IEmailAccount | null | undefined;
}

interface State {
  currentEmail: IEmailAccount;
  open: boolean;
  alertVisible: boolean;
  sendingPhoneDisplay: string;
}

class EmailSelectorSection extends React.PureComponent<Props, State> {
  static defaultProps = {
    isVisible: true,
  };

  constructor(props: Props) {
    super(props);
    const post = get(this.props.currentPost, "post", null);
    const extraFromPost = get(post, "extraEmail", null);
    const listDefault = mergeSendingEmailDefault(
      props.redisSendingEmail,
      props.firebaseDefaultSendingEmail
    );
    const currentEmail = extraFromPost != null ? extraFromPost : listDefault;

    this.state = {
      currentEmail: currentEmail,
      open: false,
      alertVisible: false,
      sendingPhoneDisplay: "",
    };
  }

  loadSendingPhoneNumber = (): void => {
    const { config, currentCharity, serviceAccessToken } = this.props;
    if (!config?.adminServiceUrl || !currentCharity?.id || !serviceAccessToken) {
      return;
    }
    getSendingPhoneNumber(
      config.adminServiceUrl,
      currentCharity.id,
      serviceAccessToken
    )
      .then(response => {
        const raw = response.phoneNumber || "";
        this.setState({
          sendingPhoneDisplay: raw
            ? formatPhoneNumber(raw.toString())
            : "—",
        });
      })
      .catch(() => {
        this.setState({ sendingPhoneDisplay: "—" });
      });
  };

  componentDidMount(): void {
    this.loadSendingPhoneNumber();
  }

  buildEmailsList = (): IEmailAccount[] => {
    return [
      mergeSendingEmailDefault(
        this.props.redisSendingEmail,
        this.props.firebaseDefaultSendingEmail
      ),
      ...this.props.extraEmailAccounts,
    ];
  };

  componentDidUpdate(prevProps: Props) {
    if (this.props.serviceAccessToken && !prevProps.serviceAccessToken) {
      this.loadSendingPhoneNumber();
    }

    const adminUrl = this.props.config?.adminServiceUrl;
    const prevAdminUrl = prevProps.config?.adminServiceUrl;
    const charityId = this.props.currentCharity?.id;
    const prevCharityId = prevProps.currentCharity?.id;

    if (
      adminUrl &&
      charityId &&
      (adminUrl !== prevAdminUrl || charityId !== prevCharityId)
    ) {
      this.loadSendingPhoneNumber();
    }

    const postExtra = get(this.props.currentPost, "post.extraEmail", null);
    const listDefault = mergeSendingEmailDefault(
      this.props.redisSendingEmail,
      this.props.firebaseDefaultSendingEmail
    );
    const prevDefault = mergeSendingEmailDefault(
      prevProps.redisSendingEmail,
      prevProps.firebaseDefaultSendingEmail
    );
    if (
      postExtra == null &&
      listDefault &&
      (prevDefault.email !== listDefault.email ||
        prevDefault.name !== listDefault.name)
    ) {
      if (
        this.state.currentEmail.email === prevDefault.email &&
        this.state.currentEmail.name === prevDefault.name
      ) {
        this.setState({ currentEmail: listDefault });
        return;
      }
    }
    const list = this.buildEmailsList();
    const idx = list.findIndex(
      e =>
        e.email === this.state.currentEmail.email &&
        e.name === this.state.currentEmail.name
    );
    this.props.updateEmailSelected(idx === 0 ? null : this.state.currentEmail);
  }

  selectItem = e => {
    const emailsList = this.buildEmailsList();
    const index = emailsList.findIndex(
      (elem, i) => this.getEmailText(elem, i) === e.currentTarget.textContent
    );
    this.setState(
      {
        currentEmail: emailsList[index],
        open: false,
      },
      () => {
        this.props.updateEmailSelected(
          index > 0 ? this.state.currentEmail : null
        );
      }
    );
  };

  getEmailText = (email: IEmailAccount, rowIndex: number) => {
    if (rowIndex === 0) {
      return email.email
        ? `${email.name} - ${email.email} [Default email]`
        : `${email.name || "Default"} [Default email]`;
    }
    return `${email.name} - ${email.email}`;
  };

  openDropdown = () => {
    this.setState({
      open: !this.state.open,
    });
  };

  showLearnMoreAlert = () => {
    this.setState({ alertVisible: true }, () => {
      window.setTimeout(() => {
        this.setState({ alertVisible: false });
      }, 4000);
    });
  };

  render() {
    if (!this.props.isVisible) return null;

    const {
      canEditPublishing,
      busyLoading,
      currentCharity,
      currentPost,
    } = this.props;

    const emailsList = this.buildEmailsList();

    const hasExtraEmails =
      emailsList.length !== 1 ||
      get(currentPost, "post.extraEmail", null) !== null;

    const disabled: boolean =
      !canEditPublishing ||
      busyLoading ||
      !isCurrentUserCollaborator(currentCharity);

    const currentRowIndex = emailsList.findIndex(
      e =>
        e.email === this.state.currentEmail.email &&
        e.name === this.state.currentEmail.name
    );

    const phoneText =
      this.state.sendingPhoneDisplay.trim() !== ""
        ? this.state.sendingPhoneDisplay
        : "—";

    return (
      <Row className={s.rowMargin}>
        <Col lg={12} xl={{ size: 8, offset: 2 }}>
          <Row>
            <Col xs={12} className={s.emailCol70}>
              <label className={s.titleLabel}>Sending Email Address</label>
              <Alert color="info" isOpen={this.state.alertVisible}>
                Now you can send ViewSparks from different email accounts,
                contact ViewSpark to learn more.
              </Alert>
              <Dropdown
                isOpen={this.state.open}
                toggle={
                  hasExtraEmails ? this.openDropdown : this.showLearnMoreAlert
                }
                disabled={disabled}
                direction="up"
                className={s.emailDropdown}
              >
                <DropdownToggle
                  caret
                  className={s.dropdown}
                  color={"none"}
                  disabled={disabled}
                >
                  {this.getEmailText(
                    this.state.currentEmail,
                    currentRowIndex >= 0 ? currentRowIndex : 0
                  )}
                </DropdownToggle>
                <DropdownMenu
                  className={s.dropdownMenu}
                  disabled={disabled}
                >
                  {emailsList.map((emailAcc, index) => (
                    <div
                      key={`sending-email-${index}-${emailAcc.email}`}
                      onClick={this.selectItem}
                      className={
                        this.state.currentEmail.email === emailAcc.email &&
                        this.state.currentEmail.name === emailAcc.name
                          ? s.dropdownItemActive
                          : s.dropdownItem
                      }
                    >
                      {this.getEmailText(emailAcc, index)}
                    </div>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </Col>
            <Col xs={12} className={`${s.phoneColumn} ${s.phoneCol30}`}>
              <div className={s.titleLabel}>Sending Phone Number</div>
              <div
                className={s.phoneReadOnly}
                role="status"
                aria-live="polite"
              >
                {phoneText}
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }
}

const mapStateToProps = state => {
  const { firebase, user } = state;
  const charityId = user.currentCharity.id;
  const firebaseEmails = get(
    firebase,
    `data.${charityId}.internal.emails.extraEmails`,
    {}
  );
  const extraEmailAccounts: IEmailAccount[] = [];
  for (const email in firebaseEmails) {
    extraEmailAccounts.push(firebaseEmails[email]);
  }

  return {
    extraEmailAccounts,
  };
};

export default serviceCaller(
  withModals(
    connect(mapStateToProps)(withStyles(s, confirmCss)(EmailSelectorSection))
  )
);
